import {find} from 'lodash';

import {sweetAPI} from 'app/store/sweet-api';
import {showMessages} from 'app/store/sweet-api/utils';
import {TEAM_ROLE_MEMBERSHIPS_API_URL, TEAM_ROLES_API_URL} from 'app/urls';
import {gt} from 'app/utils/i18n';
import {ROLETAG, TEAMTAG, USER_RTK_TAG} from 'app/utils/rtk-tags';

import {DEFAULT_PAGE_SIZE} from '../utils/constants';

import type {RolesFetchParamsType} from 'app/roles/types';
import type {
  BasicRoleSerializedType,
  BasicUserSerializedType,
  ListResponseType,
  RoleSerializedType,
  TeamSerializedType,
} from 'sweet-types';

export const extendedAPI = sweetAPI
  .enhanceEndpoints({
    addTagTypes: [ROLETAG, TEAMTAG, USER_RTK_TAG],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      // Add a new team role
      createRole: build.mutation<
        unknown,
        {
          team: TeamSerializedType;
          name: string;
        }
      >({
        query: ({team, name}) => ({
          method: 'POST',
          url: TEAM_ROLES_API_URL,
          body: {
            team: team.url,
            name: name,
          },
        }),
        invalidatesTags: () => [
          {type: ROLETAG, id: 'list'},
          {type: TEAMTAG, id: 'list'},
        ],
        onQueryStarted: (args, api) => {
          showMessages({
            onError: () => gt('Unable to add a new team role'),
          })(args, api);
        },
      }),

      // Add user to a team role
      addUserToRole: build.mutation<
        unknown,
        {
          user: BasicUserSerializedType;
          role: BasicRoleSerializedType;
        }
      >({
        query: ({user, role}) => ({
          method: 'POST',
          url: TEAM_ROLE_MEMBERSHIPS_API_URL,
          body: {
            user: user.url,
            role: role.url,
          },
        }),
        invalidatesTags: () => [
          {type: ROLETAG, id: 'list'},
          {type: TEAMTAG, id: 'list'},
          {type: USER_RTK_TAG, id: 'list'},
        ],
        onQueryStarted: (args, api) => {
          showMessages({
            onError: () => gt('Unable to add user to the role.'),
          })(args, api);
        },
      }),

      // Remove user from the role
      removeUserFromRole: build.mutation<
        unknown,
        {
          user: {url: string};
          role: RoleSerializedType;
        }
      >({
        queryFn: ({role, user}, api, extraOptions, baseQuery) => {
          // Finding the user to be removed from the role_memberships array of a specific role
          const membership = find(role.role_memberships, {
            user_url: user.url,
          });

          if (membership) {
            return baseQuery({
              method: 'DELETE',
              url: membership.url,
            }) as any;
          } else {
            return {error: {message: gt('Member not found in role')}};
          }
        },

        invalidatesTags: () => [
          {type: ROLETAG, id: 'list'},
          {type: TEAMTAG, id: 'list'},
        ],
        onQueryStarted: (args, api) => {
          showMessages({
            onError: {
              fallback: (response) =>
                response?.message ??
                gt('Unable to remove member from the role'),
              byStatus: {
                404: () => gt('Member not found in role'),
              },
            },
          })(args, api);
        },
      }),

      // Request roles
      getRoles: build.query<
        ListResponseType<RoleSerializedType>,
        RolesFetchParamsType
      >({
        query: (payload) => {
          return {
            method: 'GET',
            url: TEAM_ROLES_API_URL,
            params: payload,
          };
        },
        providesTags: () => [{type: ROLETAG, id: 'list'}],
        onQueryStarted: (args, api) => {
          showMessages({
            onError: () => gt('Could not fetch roles.'),
          })(args, api);
        },
      }),

      // Delete role
      deleteRole: build.mutation<
        unknown,
        {
          role: RoleSerializedType;
        }
      >({
        query: (payload) => ({
          method: 'DELETE',
          url: payload.role.url,
        }),
        invalidatesTags: () => [{type: TEAMTAG, id: 'list'}],
        onQueryStarted: (args, api) => {
          showMessages({
            onSuccess: () => gt('Role deleted'),
            onError: () => gt('Unable to update role'),
          })(args, api);
        },
      }),

      // Update Role
      updateRole: build.mutation<
        unknown,
        {
          roleURL: string;
          updates: Record<string, string>;
        }
      >({
        query: (payload) => ({
          method: 'PATCH',
          url: payload.roleURL,
          body: payload.updates,
        }),
        invalidatesTags: () => [{type: TEAMTAG, id: 'list'}],
        onQueryStarted: (args, api) => {
          showMessages({
            onError: () => gt('Unable to update role'),
          })(args, api);
        },
      }),
    }),
  });

// Hooks extracted from extendedAPI
export const {
  useGetRolesQuery: useGetRoles,
  useDeleteRoleMutation: useDeleteRole,
  useCreateRoleMutation: useCreateRole,
  useUpdateRoleMutation: useUpdateRole,
  useAddUserToRoleMutation: useAddUserToRole,
  useRemoveUserFromRoleMutation: useRemoveUserFromRole,
  useLazyGetRolesQuery: useLazyRoles,
} = extendedAPI;

export const convertRoleLocationParamsToFetchParams = (
  query: RolesFetchParamsType
): RolesFetchParamsType => ({
  page: query.page != null ? query.page : '1',
  page_size: query.page_size != null ? query.page_size : DEFAULT_PAGE_SIZE,
  team_id: query.team_id !== '-1' ? query.team_id : undefined,
  team_ids: query.team_ids != null ? query.team_ids : undefined,
  search: query.search != null ? query.search : undefined,
});
